/* create-diff-preview.tsx generated by @compiled/babel-plugin v0.36.1 */
import "./create-diff-preview.compiled.css";
import * as React from 'react';
import { ax, ix } from "@compiled/react/runtime";
import { JSONTransformer } from '@atlaskit/editor-json-transformer';
import { Fragment } from '@atlaskit/editor-prosemirror/model';
import { createDiffFromFragments } from '@atlassian/editor-ai-common/diff/createDiffFromFragments';
import { createDiffTransformation } from '@atlassian/editor-ai-common/diff/createDiffTransformation';
import { ignoreTrailingAndLeadingSpecialCharacters, concatConsecutiveReplacements } from '@atlassian/editor-ai-common/diff/postProcessing';
import { PreviewRenderer } from '../components/PreviewRenderer/PreviewRenderer';
var DELETED_COLOR = '#FF8F73';
var ADDED_COLOR = '#79F2C0';
var REPLACED_COLOR = '#FFE380';
var diffHighlightStylesLight = null;
var diffHighlightStylesDark = null;
var diffDeletedStyles = null;
export var createDiffPreview = function createDiffPreview(_ref) {
  var _schema$marks, _slice$content$firstC, _slice$content$lastCh;
  var editorView = _ref.editorView,
    pmFragment = _ref.pmFragment,
    positions = _ref.positions,
    providerFactory = _ref.providerFactory,
    colorMode = _ref.colorMode;
  if (pmFragment === Fragment.empty) {
    return;
  }
  var schema = editorView.state.schema;

  // If schema doesn't have textColor, underline, or strike, we won't be able to render the highlights
  if (!((_schema$marks = schema.marks) !== null && _schema$marks !== void 0 && _schema$marks.backgroundColor)) {
    return;
  }
  var slice;
  // If the positions are the same, then we know we don't have a normal range
  // selection, so we want to send the full document
  if (positions[0] === positions[1]) {
    slice = editorView.state.doc.slice(0, editorView.state.doc.content.size);
    // If the positions are different then we want to send in the positions as
    // this will be a selection the user made
  } else {
    slice = editorView.state.doc.slice(positions[0], positions[1], true);
  }

  // if the slice starts/ends at different depths, then the selection is across multiple nodes and this can cause issues
  // with the diffing algorithm. We'll just return early here.
  if (slice.openStart !== slice.openEnd) {
    return;
  }

  // Edge case for when the slice is a single line - the content will begin and end
  // with inline nodes - to make it convertable to a document we wrap it in a paragraph
  var content = (_slice$content$firstC = slice.content.firstChild) !== null && _slice$content$firstC !== void 0 && _slice$content$firstC.isInline && (_slice$content$lastCh = slice.content.lastChild) !== null && _slice$content$lastCh !== void 0 && _slice$content$lastCh.isInline ? Fragment.from(schema.nodes.paragraph.create({}, slice.content)) : slice.content;
  if (!content) {
    return;
  }

  // IMPORTANT! NOTE!
  // What follows may be a little confusing, but it's necessary to ensure that the final preview displays correctly.
  // The AI can return a fragment with more then just text changes, it can also change the markup of the content.
  // For example, it could bold a word.
  // The diffing alogrithm can't handle this, and is only designed for text changes. So we need to ensure the markup changes
  // are kept in the final preview. To do this we will diff in reverse, and use the final preview fragment as our base
  // to apply diff transformations on top of. This way we can ensure the final preview will display the correct markup changes.
  // This means diffs marked as ADDITION will be displayed as DELETION, and vice versa.
  var diffObjects = createDiffFromFragments({
    original: pmFragment,
    // This is the final preview fragment
    corrected: content,
    // this was the original document content. We're diffing in reverse.
    schema: schema,
    offset: 1,
    postProcessors: [
    // Confusable characters could be changed depending on what the AI model was trained on. This will ignore those changes.
    ignoreTrailingAndLeadingSpecialCharacters,
    // We want to avoid have multiple replacements in a row, as it can be confusing to the user. So we'll just join the
    // replacements so they're represented as a single replacement.
    concatConsecutiveReplacements]
  });
  if (!diffObjects.length) {
    return;
  }
  var docFrom = schema.nodes.doc.createAndFill({}, pmFragment);
  if (!docFrom) {
    return;
  }
  var transform = createDiffTransformation(docFrom, diffObjects, {
    onAddition: function onAddition(_ref2) {
      var transform = _ref2.transform,
        from = _ref2.from,
        to = _ref2.to,
        replacement = _ref2.replacement;
      if (!replacement) {
        return;
      }
      transform.insert(from, replacement);
      transform.addMark(from, to + replacement.size,
      // Why deleted color for ADDITION? See important note above.
      schema.marks.backgroundColor.create({
        color: DELETED_COLOR
      }));
    },
    onDeletion: function onDeletion(_ref3) {
      var transform = _ref3.transform,
        from = _ref3.from,
        to = _ref3.to;
      transform.addMark(from, to, schema.marks.backgroundColor.create({
        // Why added color for DELETION? See important note above.
        color: ADDED_COLOR
      }));
    },
    onReplacement: function onReplacement(_ref4) {
      var transform = _ref4.transform,
        from = _ref4.from,
        to = _ref4.to;
      transform.addMark(from, to, schema.marks.backgroundColor.create({
        color: REPLACED_COLOR
      }));
    }
  });
  var jsonTransformer = new JSONTransformer(schema);
  var doc;
  try {
    doc = jsonTransformer.encode(transform.doc);
  } catch (e) {
    return;
  }
  if (!doc) {
    return;
  }
  return /*#__PURE__*/React.createElement("div", {
    "data-testid": "diff-preview-renderer",
    className: ax(["_f3ge1911 _1lop7jlr _11atv77o", colorMode === 'light' ? "_4hbp13y6 _485313y6 _2hap13y6" : "_4hbptlf7 _4853tlf7 _2haptlf7"]),
    style: {
      "--_318r0m": ix("repeating-linear-gradient(\n\t\t\t90deg,\n\t\t\t".concat("var(--ds-border-accent-gray, #758195)", ",\n\t\t\t", "var(--ds-border-accent-gray, #758195)", " ", "var(--ds-space-025, 2px)", ",\n\t\t\ttransparent ", "var(--ds-space-025, 2px)", ",\n\t\t\ttransparent ", "var(--ds-space-050, 4px)", ") repeat-x left bottom / 100% ", "var(--ds-space-025, 2px)", " border-box,\n\t\t\tconic-gradient(\n\t\t\t\tfrom var(--panel-gradient-angle, 270deg),\n\t\t\t\t#0065FF1A 0%,\n\t\t\t\t#0469FF1A 20%,\n\t\t\t\t#BF63F31A 50%,\n\t\t\t\t#FFA9001A 56%,\n\t\t\t\t#0065FF1A 100%\n\t\t\t) padding-box")),
      "--_ph4f04": ix("repeating-linear-gradient(\n\t\t\t90deg,\n\t\t\t".concat("var(--ds-border-accent-gray, #758195)", ",\n\t\t\t", "var(--ds-border-accent-gray, #758195)", " ", "var(--ds-space-025, 2px)", ",\n\t\t\ttransparent ", "var(--ds-space-025, 2px)", ",\n\t\t\ttransparent ", "var(--ds-space-050, 4px)", ") repeat-x left bottom / 100% ", "var(--ds-space-025, 2px)", " border-box,\n\t\t\tconic-gradient(\n\t\t\t\tfrom var(--panel-gradient-angle, 270deg),\n\t\t\t\t#0065FF80 0%,\n\t\t\t\t#0469FF80 20%,\n\t\t\t\t#BF63F380 50%,\n\t\t\t\t#FFA90080 56%,\n\t\t\t\t#0065FF80 100%\n\t\t\t) padding-box"))
    }
  }, /*#__PURE__*/React.createElement(PreviewRenderer, {
    jsonDocument: doc,
    providerFactory: providerFactory,
    showTelepointer: false
  }));
};